import { newBlock } from "./document/documentModel";
import type { EditorBlock, EditorPage } from "./editor.types";

/** Stable identity makes retrying a partially completed conversion safe. */
export function introductionBlock(page: EditorPage, description: string): EditorBlock {
  const sourceId = `${(parseInt(page.source_page_id[0], 16) ^ 8).toString(16)}${page.source_page_id.slice(1)}`;
  return { ...newBlock(page, "text", { richText: description }), source_block_id: sourceId };
}

type IntroductionStore = {
  list: (pageId: string) => Promise<EditorBlock[]>;
  save: (pageId: string, previous: EditorBlock[], next: EditorBlock[]) => Promise<unknown>;
  clear: (versionId: string) => Promise<unknown>;
};

const conversions = new Map<string, Promise<void>>();
/** Preserve legacy homepage copy as ordinary content before removing its old field. */
export function convertIntroduction(versionId: string, page: EditorPage, description: string, store: IntroductionStore) {
  const active = conversions.get(versionId);
  if (active) return active;
  const request = (async () => {
    const intro = introductionBlock(page, description);
    const previous = await store.list(page.id);
    if (!previous.some(block => block.source_block_id === intro.source_block_id)) {
      try {
        await store.save(page.id, previous, [intro, ...previous.map(block => ({
          ...block, position: block.parent_source_block_id ? block.position : block.position + 1,
        }))]);
      } catch (error) {
        // Another editor may have completed the same conversion concurrently.
        const current = await store.list(page.id);
        if (!current.some(block => block.source_block_id === intro.source_block_id)) throw error;
      }
    }
    // Only clear the old value after the content has been saved successfully.
    await store.clear(versionId);
  })().finally(() => conversions.delete(versionId));
  conversions.set(versionId, request);
  return request;
}
